import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Save, SlidersHorizontal, Globe, FileAudio, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Audio types that have a magic-byte signature (so they can actually be uploaded + transcribed)
const SUPPORTED_AUDIO_TYPES = ["mp3", "wav", "m4a", "aac", "ogg", "flac", "webm"];
const DEFAULTS = {
  external_content_max_chars: 12000,
  external_content_min_chars: 20,
  media_presigned_ttl_seconds: 300,
  transcription_audio_types: ["mp3", "wav", "m4a", "ogg", "aac", "flac", "webm"],
};

export default function AIActorFineTuning() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actor, setActor] = useState(null);
  const [form, setForm] = useState({ ...DEFAULTS });

  useEffect(() => {
    fetchActor();
  }, [actorId]);

  const fetchActor = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActor(res.data);
      setForm({
        external_content_max_chars: res.data.external_content_max_chars ?? DEFAULTS.external_content_max_chars,
        external_content_min_chars: res.data.external_content_min_chars ?? DEFAULTS.external_content_min_chars,
        media_presigned_ttl_seconds: res.data.media_presigned_ttl_seconds ?? DEFAULTS.media_presigned_ttl_seconds,
        transcription_audio_types: res.data.transcription_audio_types ?? DEFAULTS.transcription_audio_types,
      });
    } catch (e) {
      console.error("Failed to load actor", e);
      toast.error("Failed to load actor");
    } finally {
      setLoading(false);
    }
  };

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(Number(v) || 0, hi));

  const toggleAudioType = (t) => {
    setForm((prev) => {
      const set = new Set(prev.transcription_audio_types || []);
      if (set.has(t)) set.delete(t);
      else set.add(t);
      return { ...prev, transcription_audio_types: Array.from(set) };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const payload = {
        external_content_max_chars: clamp(form.external_content_max_chars, 500, 100000),
        external_content_min_chars: clamp(form.external_content_min_chars, 0, 1000),
        media_presigned_ttl_seconds: clamp(form.media_presigned_ttl_seconds, 60, 3600),
        transcription_audio_types: form.transcription_audio_types,
      };
      await axios.put(`${API}/ai/actors/${actorId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm((prev) => ({ ...prev, ...payload }));
      toast.success("Fine tuning saved");
    } catch (e) {
      console.error("Failed to save", e);
      toast.error("Failed to save fine tuning");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ ...DEFAULTS });
    toast.info("Reset to defaults (not saved yet)");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl mx-auto p-6 min-h-screen" data-testid="fine-tuning-page">
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate(`/ai/actors/manage/${actorId}`)} className="mb-4" data-testid="fine-tuning-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-900 text-white">
            <SlidersHorizontal className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Fine Tuning</h1>
            {actor && <p className="text-muted-foreground">{actor.name}</p>}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Control how this actor handles website links and audio files shared in the public chat.
        </p>
      </div>

      <div className="space-y-6">
        {/* External content limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5" /> External Content Limits
            </CardTitle>
            <CardDescription>Applies to scraped web pages and audio transcripts injected into the chat.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-1 block">Max external content characters</label>
              <Input
                type="number"
                min={500}
                max={100000}
                value={form.external_content_max_chars}
                onChange={(e) => setForm({ ...form, external_content_max_chars: e.target.value })}
                data-testid="ft-max-chars-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Longer pages/audio are trimmed to this length before the AI reads them. Higher = more context but more cost/slower. Range 500–100000.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Minimum valid content characters</label>
              <Input
                type="number"
                min={0}
                max={1000}
                value={form.external_content_min_chars}
                onChange={(e) => setForm({ ...form, external_content_min_chars: e.target.value })}
                data-testid="ft-min-chars-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Scraped content shorter than this is treated as empty/junk and ignored. Range 0–1000.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Link expiry */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5" /> Media Link Expiry
            </CardTitle>
            <CardDescription>How long the temporary audio link stays valid for transcription.</CardDescription>
          </CardHeader>
          <CardContent>
            <label className="text-sm font-medium mb-1 block">External link expiry (seconds)</label>
            <Input
              type="number"
              min={60}
              max={3600}
              value={form.media_presigned_ttl_seconds}
              onChange={(e) => setForm({ ...form, media_presigned_ttl_seconds: e.target.value })}
              data-testid="ft-ttl-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Shorter = more secure; longer helps very large audio files finish transcribing. Range 60–3600.
            </p>
          </CardContent>
        </Card>

        {/* Audio types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileAudio className="w-5 h-5" /> Audio Types to Transcribe
            </CardTitle>
            <CardDescription>Which audio formats get transcribed when shared in chat.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {SUPPORTED_AUDIO_TYPES.map((t) => {
                const checked = (form.transcription_audio_types || []).includes(t);
                return (
                  <label
                    key={t}
                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-colors ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}
                    data-testid={`ft-audio-type-${t}`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checked}
                      onChange={() => toggleAudioType(t)}
                    />
                    <span className="text-sm font-medium uppercase">{t}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Note: a type only works if it's also enabled in the actor's "File Attachments" allowed types.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} className="flex-1" data-testid="ft-reset-btn">
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="ft-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
